import { useMemo, useState } from 'react'
import { Box, Flex, Image, Link, Portal, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { LuCheck, LuSparkles } from 'react-icons/lu'

import { useBrandContext } from '@/context/brand-context'
import { useDesignMockups } from '@/api/designs'
import '@/components/ambient/animations.css'

interface MockupHoverPanelProps {
  designId: string
  productCategory: string
  mousePoint: { x: number; y: number }
  onMouseEnter: () => void
  onMouseLeave: () => void
}

const CURSOR_OFFSET = 16
const VIEWPORT_PADDING = 16
const PANEL_WIDTH_RATIO = 0.48
const PANEL_HEIGHT_RATIO = 0.5

const BG_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Yellow', value: '#EAB308' }
]

export default function MockupHoverPanel({
  designId,
  productCategory,
  mousePoint,
  onMouseEnter,
  onMouseLeave
}: MockupHoverPanelProps) {
  const [selectedColor, setSelectedColor] = useState(BG_COLORS[0].value)
  const { brandAnalysis } = useBrandContext()
  const brandColor = useMemo(() => {
    const colors = brandAnalysis?.brand.colors ?? []
    return (
      colors.find((c) => c.usage === 'primary')?.hex ?? colors[0]?.hex ?? null
    )
  }, [brandAnalysis?.brand.colors])
  const { data: mockups } = useDesignMockups(designId)
  const completedMockups =
    mockups?.filter((m) => m.status === 'COMPLETED') ?? []

  if (!completedMockups.length) return null

  const vw = window.innerWidth
  const vh = window.innerHeight
  const panelWidth = Math.round(vw * PANEL_WIDTH_RATIO)
  const panelHeight = Math.round(vh * PANEL_HEIGHT_RATIO)

  const spaceRight = vw - mousePoint.x - CURSOR_OFFSET
  const spaceLeft = mousePoint.x - CURSOR_OFFSET
  const spaceBelow = vh - mousePoint.y - CURSOR_OFFSET
  const spaceAbove = mousePoint.y - CURSOR_OFFSET

  const fitsRight = spaceRight >= panelWidth + VIEWPORT_PADDING
  const fitsLeft = spaceLeft >= panelWidth + VIEWPORT_PADDING

  const positionStyles: React.CSSProperties = { position: 'fixed', zIndex: 1500 }
  let slideX = 0
  let slideY = 0

  if (fitsRight) {
    positionStyles.left = mousePoint.x + CURSOR_OFFSET
  } else if (fitsLeft) {
    positionStyles.left = mousePoint.x - CURSOR_OFFSET - panelWidth
  } else {
    let left = mousePoint.x - panelWidth / 2
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, vw - panelWidth - VIEWPORT_PADDING)
    )
    positionStyles.left = left
  }

  if (fitsRight || fitsLeft) {
    let top = mousePoint.y - panelHeight / 2
    top = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, vh - panelHeight - VIEWPORT_PADDING)
    )
    positionStyles.top = top
    positionStyles.maxHeight = vh - VIEWPORT_PADDING * 2
    slideX = fitsRight ? -8 : 8
  } else {
    const showBelow = spaceBelow >= panelHeight || spaceBelow >= spaceAbove
    if (showBelow) {
      positionStyles.top = mousePoint.y + CURSOR_OFFSET
      positionStyles.maxHeight = spaceBelow - VIEWPORT_PADDING
    } else {
      positionStyles.bottom = vh - mousePoint.y + CURSOR_OFFSET
      positionStyles.maxHeight = spaceAbove - VIEWPORT_PADDING
    }
    slideY = showBelow ? -8 : 8
  }

  return (
    <Portal>
      <motion.div
        initial={{ opacity: 0, x: slideX, y: slideY, scale: 0.97 }}
        animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
        exit={{ opacity: 0, x: slideX, y: slideY, scale: 0.97 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          width: panelWidth,
          overflowY: 'auto',
          ...positionStyles
        }}
      >
        <Box
          borderRadius="16px"
          bg="rgba(20, 23, 30, 0.95)"
          backdropFilter="blur(12px)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.1)"
          boxShadow="0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)"
          overflow="hidden"
        >
          <Flex>
            {/* Left column — mockup images */}
            <Box flex="3" p="14px" minW={0}>
              <Flex flexWrap="wrap" gap="8px">
                {completedMockups.map((mockup) => (
                  <Box
                    key={mockup.id}
                    borderRadius="10px"
                    overflow="hidden"
                    bg={selectedColor}
                    transition="background 0.2s ease"
                  >
                    <Image
                      src={mockup.mockupImageUrl}
                      alt="Product mockup"
                      w="100%"
                      display="block"
                      objectFit="cover"
                    />
                  </Box>
                ))}
              </Flex>
            </Box>

            {/* Right column — Pro upsell */}
            <Flex
              direction="column"
              flex="2"
              minW="200px"
              borderLeft="1px solid"
              borderColor="rgba(255,255,255,0.06)"
              bg="rgba(255,255,255,0.02)"
            >
              <Flex direction="column" flex="1" px="16px" pt="16px" pb="14px">
                <Flex align="center" gap="8px" mb="14px">
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="10px"
                    bg="rgba(0, 112, 243, 0.15)"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <LuSparkles size={16} color="#60a5fa" />
                  </Flex>
                  <Box>
                    <Text
                      textStyle="bodySuperSmall"
                      fontWeight={600}
                      color="whiteAlpha.900"
                    >
                      Design Studio
                    </Text>
                  </Box>
                </Flex>

                <Text textStyle="bodySuperSmall" color="whiteAlpha.600" mb="16px">
                  Visualize your designs on real merch instantly.
                </Text>

                {productCategory !== 'IPHONE_CASE' && (
                  <Box mb="16px">
                    <Text textStyle="caption" color="whiteAlpha.500" mb="8px">
                      Color
                    </Text>
                    <Flex align="center" gap="6px" flexWrap="wrap">
                      {BG_COLORS.map((color) => (
                        <Box
                          key={color.value}
                          as="button"
                          w="18px"
                          h="18px"
                          borderRadius="full"
                          bg={color.value}
                          border="2px solid"
                          borderColor={
                            selectedColor === color.value
                              ? 'white'
                              : 'rgba(255,255,255,0.15)'
                          }
                          boxShadow={
                            selectedColor === color.value
                              ? '0 0 0 1px rgba(255,255,255,0.3)'
                              : 'none'
                          }
                          transition="all 0.15s ease"
                          cursor="pointer"
                          _hover={{ borderColor: 'rgba(255,255,255,0.5)' }}
                          onClick={() => setSelectedColor(color.value)}
                          title={color.name}
                          flexShrink={0}
                        />
                      ))}
                    </Flex>
                  </Box>
                )}

                <Flex direction="column" gap="10px" flex="1">
                  {[
                    'High quality products',
                    'Variety of colors',
                    '50+ product templates',
                    'One-click buy'
                  ].map((feature) => (
                    <Flex key={feature} align="center" gap="8px">
                      <Flex
                        w="18px"
                        h="18px"
                        borderRadius="full"
                        bg="rgba(34, 197, 94, 0.12)"
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <LuCheck size={10} color="#22c55e" />
                      </Flex>
                      <Text textStyle="bodySuperSmall" color="whiteAlpha.700">
                        {feature}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </Flex>

              <Box px="16px" pb="14px">
                <Link
                  href="mailto:michael@spacerunners.com"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="6px"
                  h="36px"
                  textStyle="bodySmall"
                  fontWeight={500}
                  borderRadius="9999px"
                  bg={brandColor ?? 'accent.500'}
                  color="white"
                  transition="all 0.3s ease"
                  _hover={{
                    opacity: 0.85,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)',
                    textDecoration: 'none'
                  }}
                >
                  <LuSparkles size={12} />
                  Unlock Pro
                </Link>
              </Box>
            </Flex>
          </Flex>
        </Box>
      </motion.div>
    </Portal>
  )
}
